import React, { useState, useEffect } from "react";
import CustomDropdown from "../../../common/dropdown";
import { resumeUpload } from "../../../supports/resumeUpload";
import { generateRequestId } from "../../../common/common";
import { RecruiterApi } from "../../../supports/recruiterApi";
import Loader from "../../../common/loader";
import { useToast } from "../../../common/toastSystem";
import unifiedAuthManager from "../../../services/unifiedAuthManager";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateUser = ({ user, onBack }) => {
    const [sectorList, setSectorList] = useState([]);
    const [languageList, setLanguageList] = useState([]);
    const [rolesList, setRolesList] = useState([]);
    const [isEditUser, setIsEditUser] = useState(false);
    const [form, setForm] = useState({
        login: "",
        first_name: "",
        last_name: "",
        email: "",
        sector: "",
        role: [],
        broadbean_username: "",
        language: "",
    });
    const [errors, setErrors] = useState({
        email: "",
        broadbean_username: "",
    });
    const [submitErrors, setSubmitErrors] = useState({});
    const toast = useToast();
    const [isEditCalled, setIsEditCalled] = useState(false);
    const userData = unifiedAuthManager.getUnifiedUser();

    useEffect(() => {
        fetchSectors();
        fetchLanguages();
        fetchAllRoles();
    }, []);

    let user_payload = {
        user_payload: {
            request_id: generateRequestId(),
            user_id: userData.id,
            tenant: 'MPH',
            email: userData.email
        },
    }

    const fetchSectors = async () => {
        try {
            const res = await resumeUpload.getSectors(user_payload)
            if (res.success) {
                setSectorList(res.data?.map(item => ({
                    label: item.name,
                    value: item.id
                })));
            } else {
                setSectorList([])
            }
        } catch (err) {
            setSectorList([])
            console.error("Error loading sectors:", err);
        }
    };

    const fetchLanguages = async () => {
        try {
            const res = await resumeUpload.getLanguages(user_payload)
            if (res.success) {
                setLanguageList(res.data?.map(item => ({
                    label: item.language,
                    value: item.code
                })));
            } else {
                setLanguageList([])
            }
        } catch (err) {
            setLanguageList([])
            console.error("Error loading languages:", err);
        }
    };

    const fetchAllRoles = async () => {
        try {
            const res = await RecruiterApi.getAllRoles(user_payload)
            if (res.success) {
                setRolesList(res.data.roles?.map(item => ({
                    role_name: item.role_name,
                    role_id: item.role_id
                })));
            } else {
                setRolesList([])
            }
        } catch (err) {
            setRolesList([])
            console.error("Error loading roles:", err);
        }
    };

    useEffect(() => {
        if (user) {
            setForm({
                login: user.login,
                first_name: user?.first_name || "",
                last_name: user?.last_name || "",
                email: user?.email || "",
                sector: user.sectors || [],
                role: user?.roles || [],
                broadbean_username: user?.broadbean_username || "",
                language: user.lang_key,
            });
            setIsEditUser(true)
        }
    }, [user]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (field === "email") {
            setErrors(prev => ({
                ...prev,
                email: value && !emailRegex.test(value) ? "Invalid email address" : "",
            }));
        }
        if (field === "broadbean_username") {
            setErrors(prev => ({
                ...prev,
                broadbean_username: value && !emailRegex.test(value) ? "Invalid email address" : "",
            }));
        }
        setSubmitErrors({});
    };

    const editFieldsReady = () => {
        if (sectorList.length > 0 && languageList.length > 0 && rolesList.length > 0) {
            return true
        }
        return false
    }

    const getLanguageValue = () => {
        const match = languageList?.find(lang => lang.code === form.language);
        return match ? match.language : form.language;
    }

    const getPayload = () => {
        const roleIds = Array.isArray(form.role)
            ? form.role.map(r => r.role_id)
            : [];
        const sectorIds = Array.isArray(form.sector)
            ? form.sector.map(s => s.sector_id || s.value)
            : [];
        console.log("Selected sectors:", form.sector, "Mapped sector IDs:", sectorIds);
        let languageCode = form.language;
        if (languageList && form.language) {
            const langObj = languageList.find(
                lang => lang.label === form.language || lang.value === form.language
            );
            if (langObj) {
                languageCode = langObj.label;
            }
        }
        const payload = {
            user_payload: {
                user_id: userData.id,
                request_id: generateRequestId(),
                email: userData.email,
                tenant: "GBB"
            },
            login: form.login,
            first_name: form.first_name,
            last_name: form.last_name,
            role_ids: roleIds,
            language: languageCode,
            sector_ids: sectorIds,
            broadbean_username: form.broadbean_username,
            ...(isEditUser && user?.user_id ? { user_id: user.user_id } : {}),
            ...(!isEditUser ? { email: form.email, } : {})
        };
        return payload
    }

   const validateFormFields = () => {
        let valid = true;

        if (!form.login || !form.login.trim()) {
            valid = false;
        }
        if (!form.first_name || !form.first_name.trim()) {
            valid = false;
        }
        if (!form.last_name || !form.last_name.trim()) {
            valid = false;
        }
        if (!form.email || !form.email.trim()) {
            valid = false;
        } else if (!emailRegex.test(form.email)) {
            valid = false;
        }
        if (!form.sector || form.sector?.length === 0) {
            valid = false;
        }
        if (!form.role || form.role.length === 0) {
            valid = false;
        }
        if (!form.language) {
            valid = false;
        }
        if (form.broadbean_username && !emailRegex.test(form.broadbean_username)) {
            valid = false;
        }

        return valid;
    }

    const validateForm = () => {
        let valid = true;
        let validationErrors = {};

        if (!form.login || !form.login.trim()) {
            validationErrors.login = "Username is required";
            valid = false;
        }
        if (!form.first_name || !form.first_name.trim()) {
            validationErrors.first_name = "First name is required";
            valid = false;
        }
        if (!form.last_name || !form.last_name.trim()) {
            validationErrors.last_name = "Last name is required";
            valid = false;
        }
        if (!form.email || !form.email.trim()) {
            validationErrors.email = "Email is required";
            valid = false;
        } else if (!emailRegex.test(form.email)) {
            validationErrors.email = "Invalid email address";
            valid = false;
        }
        if (!form.sector) {
            validationErrors.sector = "Sector is required";
            valid = false;
        }
        if (!form.role || form.role.length === 0) {
            validationErrors.role = "At least one role is required";
            valid = false;
        }
        if (!form.language) {
            validationErrors.language = "Language is required";
            valid = false;
        }
        if (form.broadbean_username && !emailRegex.test(form.broadbean_username)) {
            validationErrors.broadbean_username = "Invalid email address";
            valid = false;
        }
        // else if (!form.broadbean_username) {
        //     validationErrors.broadbean_username = "Broadbean username is required";
        //     valid = false;
        // }
        setSubmitErrors(validationErrors);
        return { isValid: valid, errors: validationErrors };
    }

    const handleAddUser = async () => {
        const { isValid } = validateForm();
        if (!isValid) {
            toast.warning("Please complete all fields to continue.");
            return;
        }
        if (isEditCalled) return
        setIsEditCalled(true)
        try {
            const res = await RecruiterApi.addUser(getPayload())
            setIsEditCalled(false)
            if (res.success) {
                onBack()
                toast.success("User created successfully. Please activate your account using the link sent to your email.",7000)
            } else {
                toast.warning(res.message || "Failed to add new user!")
            }
        } catch (err) {
            setIsEditCalled(false)
            toast.warning("Failed to add new user!")
            console.error("Error adding user:", err);
        }
    };

    const handleEditUser = async () => {
        const { isValid } = validateForm();
        if (!isValid) {
            toast.warning("Please fix the errors in the form before saving.");
            return;
        }
        if (isEditCalled) return
        setIsEditCalled(true)
        console.log("Edit payload:", getPayload());
        try {
            const res = await RecruiterApi.editUser(getPayload())
            setIsEditCalled(false)
            if (res.success) {
                onBack()
                toast.success("Edited successfully!")
            } else {
                toast.warning(res.message || "Failed to edit!")
            }
        } catch (err) {
            setIsEditCalled(false)
            toast.warning("Failed to edit!")
            console.error("Error adding user:", err);
        }
    };

    const MultiSelectDropdown = ({
        options = [],
        value = [],
        onChange,
        placeholder = "Select",
    }) => {
        const [isOpen, setIsOpen] = useState(false);
        const toggleOption = (item) => {
            let updated;
            if (value.some(v => v.role_id === item.role_id)) {
                updated = value.filter(v => v.role_id !== item.role_id);
            } else {
                updated = [...value, item];
            }
            onChange(updated);
        };

        return (
            <div className="relative w-full text-xs">
        <div
            className="border border-gray-300 rounded px-2 py-1.5 w-full flex justify-between items-center text-gray-700 bg-white min-h-[36px] cursor-pointer transition-all duration-150"
            onClick={() => setIsOpen(!isOpen)}
        >
            {/* Added flex-wrap and gap-1 here */}
            <div className="flex flex-wrap gap-1 items-center flex-1">
                {value.length === 0 ? (
                    <span className="text-gray-400">{placeholder}</span>
                ) : (
                    value.map((item) => (
                        <span
                            key={item.role_id}
                            className="flex items-center mph-light mph-primary px-2 py-0.5 rounded-full gap-1 whitespace-nowrap"
                        >
                            {item.role_name}
                            <span
                                className="cursor-pointer hover:text-red-500"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleOption(item);
                                }}
                            >
                                ✕
                            </span>
                        </span>
                    ))
                )}
            </div>

            <span className="ml-2 text-gray-400">▾</span>
        </div>

        {isOpen && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg overflow-auto max-h-60">
                {options.map((item) => {
                    const selected = value.some(v => v.role_id === item.role_id);
                    return (
                        <div
                            key={item.role_id}
                            className={`text-gray-700 text-xs px-3 py-2 cursor-pointer flex justify-between hover:bg-gray-100 ${selected ? "bg-gray-200" : ""}`}
                            onClick={() => toggleOption(item)}
                        >
                            {item.role_name}
                        </div>
                    );
                })}
            </div>
        )}
    </div>
        );
    };


    const MultiSelectDropdownSector = ({
        options = [],
        value = [],
        onChange,
        placeholder = "Select",
    }) => {
        const [isOpen, setIsOpen] = useState(false);
        const toggleOption = (item) => {
            let updated;
            if (value.some(v => v.sector_id === (item?.value ?? item?.sector_id))) {
                updated = value.filter(v => v.sector_id !== (item?.value ?? item?.sector_id));
            } else {
                updated = [...value, { sector_id: item.value, sector_name: item.label }];
            }
            onChange(updated);
        };

        return (
            <div className="relative w-full text-xs">
        <div
            className="border border-gray-300 rounded px-2 py-1.5 w-full flex justify-between items-center text-gray-700 bg-white min-h-[36px] cursor-pointer transition-all duration-150"
            onClick={() => setIsOpen(!isOpen)}
        >
            {/* Added flex-wrap and gap-1 here */}
            <div className="flex flex-wrap gap-1 items-center flex-1">
                {value.length === 0 ? (
                    <span className="text-gray-400">{placeholder}</span>
                ) : (
                    value.map((item) => (
                        <span
                            key={item.sector_id}
                            className="flex items-center mph-light mph-primary px-2 py-0.5 rounded-full gap-1 whitespace-nowrap"
                        >
                            {item.sector_name}
                            <span
                                className="cursor-pointer hover:text-red-500"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleOption(item);
                                }}
                            >
                                ✕
                            </span>
                        </span>
                    ))
                )}
            </div>

            <span className="ml-2 text-gray-400">▾</span>
        </div>

        {isOpen && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg overflow-auto max-h-60">
                {options.map((item) => {
                    const selected = value.some(v => v.sector_id === item.value);
                    return (
                        <div
                            key={item.value}
                            className={`text-gray-700 text-xs px-3 py-2 cursor-pointer flex justify-between hover:bg-gray-100 ${selected ? "bg-gray-200" : ""}`}
                            onClick={() => toggleOption(item)}
                        >
                            {item.label}
                        </div>
                    );
                })}
            </div>
        )}
    </div>
        );
    };

    return (
        <div className="backdrop-blur-sm px-5 py-4 w-full anim-slideUp">
            <div className="w-full">
                <div className="text-left">
                    <h3 className="text-sm font-semibold text-gray-800">
                        {isEditUser ? "Edit Recruiter" : "Create New Recruiter"}
                    </h3>
                    <p className="text-xs text-gray-500">
                        {isEditUser
                            ? "Edit the details of this recruiter."
                            : "Add a new user to the system with appropriate roles and permissions"}
                    </p>
                </div>

                <div className="bg-white h-[calc(100vh-9rem)] rounded px-4 py-2 text-xs mt-4 rounded-lg shadow-md">
                    {!editFieldsReady() ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader message='Loading required data' />
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col gap-6 h-[calc(100vh-13rem)] overflow-auto anim-slideUp">
                                <div className="flex flex-col gap-1 ">
                                    <p>Username <span className="text-red-500">*</span></p>
                                    <input type="text" className="w-full border border-gray-200 rounded-lg p-2" value={form.login} autoComplete="off"
                                        onChange={(e) => handleChange("login", e.target.value)}></input>
                                    {submitErrors.login && (
                                        <span className="text-red-500">{submitErrors.login}</span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <div className="flex flex-col gap-1 w-1/2">
                                        <p>First Name <span className="text-red-500">*</span></p>
                                        <input type="text" className="w-full border border-gray-200 rounded-lg p-2" value={form.first_name} autoComplete="off"
                                            onChange={(e) => handleChange("first_name", e.target.value)}></input>
                                        {submitErrors.first_name && (
                                            <span className="text-red-500">{submitErrors.first_name}</span>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1 w-1/2">
                                        <p>Last Name <span className="text-red-500">*</span></p>
                                        <input type="text" className="w-full border border-gray-200 rounded-lg p-2" value={form.last_name} autoComplete="off"
                                            onChange={(e) => handleChange("last_name", e.target.value)}></input>
                                        {submitErrors.last_name && (
                                            <span className="text-red-500">{submitErrors.last_name}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <p>Email <span className="text-red-500">*</span></p>
                                    <input
                                        autoComplete="off"
                                        type="text"
                                        className="w-full border border-gray-200 rounded-lg p-2"
                                        value={form.email}
                                        disabled={form.email && isEditUser}
                                        onChange={(e) => handleChange("email", e.target.value)}
                                    />
                                    {(errors.email || submitErrors.email) && (
                                        <span className="text-red-500">{errors.email || submitErrors.email}</span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <div className="flex flex-col gap-1 w-1/2">
                                        <p>Sector <span className="text-red-500">*</span></p>
    <MultiSelectDropdownSector
                                            options={sectorList}
                                            value={form.sector}
                                            onChange={(selected) => handleChange("sector", selected)}
                                            placeholder="Select sectors"
                                        />
                                        {submitErrors.sector && (
                                            <span className="text-red-500">{submitErrors.sector}</span>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1 w-1/2">
                                        <p>Role <span className="text-red-500">*</span></p>
                                        <MultiSelectDropdown
                                            options={rolesList}
                                            value={form.role}
                                            onChange={(selected) => handleChange("role", selected)}
                                            placeholder="Select roles"
                                        />
                                        {submitErrors.role && (
                                            <span className="text-red-500">{submitErrors.role}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {Array.isArray(form.role) && form.role.some(
                                        r => (r.role_name && ["ROLE_ADMIN", "ROLE_HR"].includes(r.role_name))
                                    ) && (
                                            <div className="flex flex-col gap-1 w-1/2">
                                                <p>Broadbean Account</p>
                                                <input
                                                    type="text"
                                                    autoComplete="off"
                                                    className="w-full border border-gray-200 rounded-lg p-2"
                                                    value={form.broadbean_username}
                                                    onChange={(e) => handleChange("broadbean_username", e.target.value)}
                                                />
                                                {((form.broadbean_username && errors.broadbean_username) || submitErrors.broadbean_username) && (
                                                    <span className="text-red-500">{errors.broadbean_username || submitErrors.broadbean_username}</span>
                                                )}
                                            </div>
                                        )}
                                    <div className="flex gap-2 w-1/2">
                                        <div className="flex flex-col gap-1 w-full">
                                            <p>Language <span className="text-red-500">*</span></p>
                                            <CustomDropdown
                                                options={languageList}
                                                value={getLanguageValue()}
                                                onChange={(value) => handleChange("language", value)}
                                                placeholder="Select language"
                                            />
                                            {submitErrors.language && (
                                                <span className="text-red-500">{submitErrors.language}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="py-2 flex justify-end gap-2">
                                <button
                                    onClick={onBack}
                                    className="p-2 px-4 text-xs rounded-full border border-gray-300 hover:bg-gray-100 transition-all"
                                >
                                    Cancel
                                </button>
                                {isEditUser ? (
                                    <button
                                        disabled={isEditCalled || validateFormFields() === false}
                                        className={`p-2 px-6 text-xs rounded-full bg-gradient text-white hover:shadow-lg transition-all ${(isEditCalled ||  validateFormFields() === false ) ? 'cursor-not-allowed opacity-50' : ''}`}
                                        onClick={handleEditUser}
                                    >
                                        {isEditCalled ? "Saving..." : "Save"}
                                    </button>
                                ) : (
                                    <button
                                        disabled={isEditCalled}
                                        className={`p-2 px-6 text-xs rounded-full bg-gradient text-white hover:shadow-lg transition-all ${isEditCalled ? 'cursor-not-allowed' : ''}`}
                                        onClick={handleAddUser}
                                    >
                                        {isEditCalled ? "Saving..." : "Add"}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CreateUser;